import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CommandPanel } from "../components/fleet/CommandPanel";
import { ConfigurationReport } from "../components/fleet/ConfigurationReport";
import { DriverAccess } from "../components/fleet/DriverAccess";
import { HeartbeatInterval } from "../components/fleet/HeartbeatInterval";
import { MessageTail } from "../components/fleet/MessageTail";
import { PublicAccessPanel } from "../components/fleet/PublicAccessPanel";
import { HiddenNotice, RetireStation } from "../components/fleet/RetireStation";
import { RotatePassword } from "../components/fleet/RotatePassword";
import { StationName } from "../components/fleet/StationName";
import { UptimeHistory } from "../components/fleet/UptimeHistory";
import { RecentSessions } from "../components/session/RecentSessions";
import { PageHeader } from "../components/shell/PageHeader";
import { ErrorNotice, Notice } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SettingGroup } from "../components/ui/SettingRow";
import { ConnectionStatus, ConnectorStatus, RegistrationPill } from "../components/ui/Status";
import { Datum, Metric, MicroLabel, SectionTitle } from "../components/ui/Text";
import { ApiError, get, post } from "../lib/api";
import { driftLede } from "../lib/configuration";
import { bayIds, connectorLabel } from "../lib/evse";
import { since, sinceParts, timestamp } from "../lib/format";
import { SERVER_PROTOCOLS, protocolLabel, refusedOfferText } from "../lib/protocol";
import { useSession } from "../lib/session";
import { retirement } from "../lib/station";
import { connectorTone } from "../lib/status";
import type { CommandResult, ChargePointDetail as Detail } from "../lib/types";

/**
 * One charge point: identity, connectors, remote commands, and the raw frames.
 *
 * The page polls at the same cadence as the fleet list. It is the view an
 * operator keeps open while power-cycling a station, so a stale connector status
 * here is the difference between "it came back" and a support call. The masthead
 * therefore states the data's own age (`ui/Freshness`) — a frozen page and a live
 * one are otherwise indistinguishable.
 *
 * ## Sections appear when they have something to say
 *
 * A station that has never booted has no connectors, no sessions, no configuration
 * report and no frames, and rendering four headings over four paragraphs of "not
 * yet" buries the one fact that matters: it has never connected. So that fact gets
 * said once, at the top, and the empty sections are not drawn at all. Sections
 * reappear on their own as the station starts reporting.
 */

const POLL_MS = 10_000;

export function ChargePointDetail() {
  const { chargePointId = "" } = useParams();
  const queryClient = useQueryClient();
  const { can } = useSession();

  const [result, setResult] = useState<
    { readonly tone: "ok" | "error"; readonly text: string } | undefined
  >(undefined);
  const detailKey = ["charge-point", chargePointId] as const;

  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () => get<Detail>(`/charge-points/${encodeURIComponent(chargePointId)}`),
    refetchInterval: POLL_MS,
    enabled: chargePointId !== "",
  });

  const command = useMutation({
    mutationFn: (input: { command: string; payload: Record<string, unknown> }) =>
      post<CommandResult>(
        `/charge-points/${encodeURIComponent(chargePointId)}/commands/${encodeURIComponent(input.command)}`,
        input.payload,
      ),
    onSuccess: (data) => {
      setResult({
        tone: "ok",
        // The station's own answer, verbatim. `Accepted` and `Rejected` are both
        // successful round trips and the operator needs to see which they got —
        // reporting "command sent" for a Rejected response would be a lie.
        text: `${data.command} → ${describeResponse(data.response)}`,
      });
      // The frame that was just sent is now in the log, and a connector status
      // may have changed as a result.
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "The command failed.";

      setResult({
        tone: "error",
        /**
         * The retry caveat, said here rather than standing permanently under the
         * form. A command is sent synchronously with a 30 second ceiling and is
         * never retried (spec 4.3 — a duplicated RemoteStartTransaction starts two
         * sessions), so a timeout genuinely leaves the outcome unknown. That is
         * worth a sentence at the moment it happens and noise at every other
         * moment.
         */
        text: isTimeout(error)
          ? `${message} The station may still have carried it out — check the frames below before sending again.`
          : message,
      });
    },
  });

  if (detail.isError) {
    return (
      <>
        <PageHeader eyebrow="Charge point" title={chargePointId || "Unknown"} />
        <ErrorNotice error={detail.error} onRetry={() => void detail.refetch()} />
        <Link
          to="/fleet"
          className="self-start text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
        >
          Back to charge points
        </Link>
      </>
    );
  }

  const row = detail.data;
  const zone = row?.depotTimezone ?? "UTC";
  const retireAction = row === undefined ? undefined : retirement(row, can);
  const canRotate = can("charge_points.rotate_password");
  const heartbeat = sinceParts(row?.lastHeartbeatAt);
  const faultedConnectors =
    row === undefined
      ? []
      : row.connectors.filter(
          (connector) => connectorTone(connector.status, connector.errorCode) === "faulted",
        );

  /**
   * Registered, but it has never completed a BootNotification.
   *
   * Distinguished from "offline" throughout this page, because they call for
   * different work: an offline station was working and stopped, so the question is
   * what changed; a station that has never booted has nothing to diagnose yet —
   * someone still has to put the credential and the URL into it.
   */
  const isRefusedForProtocol = row !== undefined && row.refusedSubprotocols !== null;
  /**
   * A refused station *has* tried to connect, and "never connected" would send
   * someone to check the URL and the credential — both of which it got right.
   */
  const hasNeverConnected =
    row !== undefined &&
    !row.connected &&
    !isRefusedForProtocol &&
    row.lastBootAt === null &&
    row.lastHeartbeatAt === null;

  return (
    <>
      <PageHeader
        eyebrow={row === undefined ? "Charge point" : `${row.depotName} · ${row.depotTimezone}`}
        title={chargePointId}
        metrics={
          row === undefined ? undefined : (
            <>
              <Metric label="EVSEs" value={String(bayIds(row.connectors).length)} />
              <Metric
                label="Faults"
                value={String(faultedConnectors.length)}
                tone={faultedConnectors.length > 0 ? "signal" : "muted"}
              />
              <Metric
                label="Heartbeat"
                value={heartbeat.value}
                {...(heartbeat.unit === undefined ? {} : { unit: heartbeat.unit })}
                hint={`every ${row.heartbeatIntervalS}s`}
              />
            </>
          )
        }
        lede={driftLede(row)}
        freshness={
          <Freshness
            updatedAt={detail.dataUpdatedAt}
            isFetching={detail.isFetching}
            intervalMs={POLL_MS}
          />
        }
      />

      {row === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <>
          {/*
            The status strip: facts only. The one link here is the diagnostic that
            belongs beside "Offline" — what the station tried when it last knocked —
            and it is most useful exactly when nothing below has anything to show.
          */}
          <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-b border-rule pb-3">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <ConnectionStatus
                connected={row.connected}
                stale={!row.connected && row.lastHeartbeatAt !== null}
              />
              <RegistrationPill status={row.registrationStatus} />
              <span className="inline-flex items-baseline gap-2">
                <MicroLabel>Last boot</MicroLabel>
                <span className="text-data text-ink-muted">
                  {timestamp(row.lastBootAt, zone)}
                  {row.lastBootReason === null ? "" : ` · ${row.lastBootReason}`}
                </span>
              </span>
            </div>
            <Link
              to={`/connections?chargePointId=${encodeURIComponent(chargePointId)}`}
              className="text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline"
            >
              Connection attempts
            </Link>
          </div>

          {row.hiddenAt === null ? null : (
            <HiddenNotice station={row} zone={zone} canUnhide={can("charge_points.hide")} />
          )}

          {row.refusedSubprotocols === null ? null : (
            <div className="flex flex-col gap-2 border-l-2 border-state-faulted bg-paper-raised px-4 py-3">
              <MicroLabel className="text-state-faulted">Unsupported OCPP version</MicroLabel>
              <p className="m-0 max-w-prose text-body text-ink">
                {refusalExplanation(row.refusedSubprotocols, row.ocppVersion)}
              </p>
              <span className="text-micro tracking-normal normal-case text-ink-muted">
                Last refused {timestamp(row.subprotocolRefusedAt, zone)}
              </span>
            </div>
          )}

          {hasNeverConnected ? (
            <div className="flex flex-col gap-2 border-l-2 border-state-transitional bg-paper-raised px-4 py-3">
              <MicroLabel className="text-state-transitional">Never connected</MicroLabel>
              <p className="m-0 max-w-prose text-body text-ink">
                This station is registered but has never completed a BootNotification, so it has no
                connectors, sessions or frames yet. Configure the WebSocket URL and the credential
                minted at registration on the station itself; it appears here within one boot.
              </p>
            </div>
          ) : null}

          {/*
            What the station is and how it has been doing, side by side: the
            reported facts across three columns, its uptime in the fourth. A station
            that has never connected has no uptime to show, and the facts take the
            full width.
          */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 xl:grid-cols-4">
            <div
              className={`flex min-w-0 flex-col gap-6 ${hasNeverConnected ? "xl:col-span-4" : "xl:col-span-3"}`}
            >
              {row.connectors.length === 0 ? null : (
                <section className="flex flex-col gap-4">
                  <SectionTitle>Connectors</SectionTitle>
                  <ul className="m-0 grid list-none grid-cols-1 gap-x-8 gap-y-3 p-0 md:grid-cols-2 xl:grid-cols-3">
                    {row.connectors.map((connector) => (
                      <li
                        key={`${connector.evseId}/${connector.connectorId}`}
                        className="flex flex-col gap-1.5 border-t border-rule pt-2"
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <MicroLabel>{connectorLabel(connector, row.connectors)}</MicroLabel>
                          <span className="text-micro tracking-normal normal-case text-ink-faint">
                            {since(connector.statusUpdatedAt)}
                          </span>
                        </div>
                        <ConnectorStatus
                          status={connector.status}
                          errorCode={connector.errorCode}
                        />
                        {connector.connectorType === null ? null : (
                          <span className="text-micro tracking-normal normal-case text-ink-muted">
                            {connector.connectorType}
                          </span>
                        )}
                        {connector.info === null && connector.vendorErrorCode === null ? null : (
                          <span className="text-micro tracking-normal normal-case text-ink-muted">
                            {[connector.vendorErrorCode, connector.info]
                              .filter((part) => part !== null)
                              .join(" · ")}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="flex flex-col gap-4">
                <SectionTitle>Station</SectionTitle>
                {/*
                  What the station reported about itself, and nothing it did not. The
                  heartbeat interval is not here: it is a setting, and it is in Settings.
                */}
                <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3 xl:grid-cols-4">
                  <Datum label="Vendor">{row.vendor ?? "—"}</Datum>
                  <Datum label="Model">{row.model ?? "—"}</Datum>
                  <Datum label="Serial" mono>
                    {row.serial ?? "—"}
                  </Datum>
                  <Datum label="Firmware" mono>
                    {row.firmwareVersion ?? "—"}
                  </Datum>
                  <Datum label="Protocol">
                    {row.ocppVersion === null ? "—" : protocolLabel(row.ocppVersion)}
                  </Datum>
                  <Datum label="Meter interval">
                    {row.effectiveMeterIntervalS === null
                      ? "not set"
                      : `${row.effectiveMeterIntervalS}s`}
                  </Datum>
                </dl>
              </section>
            </div>
            {hasNeverConnected ? null : (
              <div className="min-w-0 xl:col-span-1">
                <UptimeHistory chargePointId={chargePointId} />
              </div>
            )}
          </div>

          {/*
            ## The actions, together

            Everything an operator can do to this station sits in three consecutive
            blocks, from most to least frequent: commands to the station itself,
            settings that shape how it is sold and checked on, and — at the very
            bottom of the page, after everything there is to read — the two actions
            that cannot be taken back. Each block shows its rows whatever the
            operator's scope and draws only the buttons that would work.
          */}
          {can("charge_points.command") ? (
            <section className="flex flex-col gap-4">
              <SectionTitle
                action={
                  row.connected ? undefined : (
                    <MicroLabel className="text-signal">Not connected</MicroLabel>
                  )
                }
              >
                Remote commands
              </SectionTitle>

              {result === undefined ? null : <Notice tone={result.tone}>{result.text}</Notice>}

              <CommandPanel
                // A station that reconnects on the other version gets a fresh
                // panel: a command and values chosen for one version's form
                // must not survive into the other's.
                key={row.commandVersion}
                commands={row.availableCommands}
                ocppVersion={row.commandVersion}
                connected={row.connected}
                pending={command.isPending}
                onSend={(input) => {
                  setResult(undefined);
                  command.mutate(input);
                }}
              />
            </section>
          ) : null}

          {/*
            Name before price, because it is the cheaper decision and the one that
            can be made at any time; the heartbeat last, being plumbing rather than
            anything a driver sees.
          */}
          <SettingGroup title="Settings">
            <StationName station={row} canEdit={can("charge_points.update_sale")} />
            <PublicAccessPanel station={row} canEdit={can("charge_points.update_sale")} />
            <HeartbeatInterval station={row} canEdit={can("charge_points.update_heartbeat")} />
          </SettingGroup>

          {/*
            Directly after the settings, because the codes and the price are two
            halves of one decision: a sticker is what makes the price reachable.
            Shown whatever the operator's scope — an installer with read access is
            exactly who needs to print them.
          */}
          <DriverAccess station={row} />

          <RecentSessions chargePointId={chargePointId} hideWhenEmpty={hasNeverConnected} />

          {row.configReport === null || row.configReport.length === 0 ? null : (
            <ConfigurationReport
              report={row.configReport}
              checkedAt={row.configCheckedAt}
              zone={zone}
            />
          )}

          {row.recentMessages.length === 0 ? null : (
            <MessageTail
              chargePointId={chargePointId}
              initial={row.recentMessages}
              knownActions={row.frameActions}
              timeZone={zone}
            />
          )}

          {/*
            Last on the page. A hidden station's way back is the banner at the top,
            so only hide and delete are offered here.
          */}
          {canRotate || retireAction === "hide" || retireAction === "delete" ? (
            <SettingGroup title="Danger zone">
              {canRotate ? (
                <RotatePassword chargePointId={chargePointId} connected={row.connected} />
              ) : null}
              {retireAction === "hide" || retireAction === "delete" ? (
                <RetireStation station={row} action={retireAction} />
              ) : null}
            </SettingGroup>
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * A command that never came back, as opposed to one the station refused.
 *
 * The server answers a timeout with 504 (`api/routes/chargePoints.ts`), which is
 * the one failure where the operator must not simply press send again.
 */
function isTimeout(error: unknown): boolean {
  return error instanceof ApiError && error.status === 504;
}

/**
 * An OCPP CallResult payload is an object whose useful field is usually a status.
 * Pulled out when present rather than printing `{"status":"Accepted"}` at an
 * operator, with the full frame still available in the message tail below.
 */
function describeResponse(response: unknown): string {
  if (typeof response !== "object" || response === null) return String(response);

  const record = response as Record<string, unknown>;
  if (typeof record["status"] === "string") return record["status"];

  const keys = Object.keys(record);
  return keys.length === 0 ? "empty response" : JSON.stringify(response);
}

/**
 * Why a station is being turned away at the handshake, and what to change on
 * it. One string rather than JSX with interpolations, so the punctuation around
 * the lists cannot drift with the formatter's line breaks.
 */
function refusalExplanation(offered: readonly string[], lastVersion: string | null): string {
  const spoken = SERVER_PROTOCOLS.map(protocolLabel);
  const history =
    lastVersion === null
      ? ""
      : ` It last connected as ${protocolLabel(lastVersion)} — its configuration or firmware has likely changed since.`;

  return (
    `This station authenticated but offered ${refusedOfferText(offered)}, and this server ` +
    `speaks only ${spoken.join(" and ")} — so every connection it makes is closed at the ` +
    `handshake. Set the station's OCPP version to ${spoken.join(" or ")} in its ` +
    `configuration tool; this notice clears on its next connection.${history}`
  );
}
